import { useGetAccountQueryOptions } from "@/api/account/account"
import { useGetCustomerQueryOptions } from "@/api/customer/customer"
import { useGetRenewalEligibilityQueryOptions } from "@/api/renewals/renewals"
import { MyAccountRoute } from "@/constants/routes/myAccount"
import { setAnalyticsUser } from "@/lib/useAnalyticsTracking"
import ErrorReporter from "@/services/ErrorReporter"
import TrackingService from "@/services/TrackingService"
import { QueryClient, useQueryClient } from "@tanstack/vue-query"
import { NavigationGuard } from "vue-router"

import { getIneligibleRenewalRoute } from "@/views/myaccount/renewal/renewalEligibility"

// Identify first: on a cold load the app has not yet sent GA4 the account number
const trackEntry = async (queryClient: QueryClient, accountId: number) => {
  try {
    const { accountNumber } = await queryClient.ensureQueryData(
      useGetAccountQueryOptions(accountId),
    )
    if (accountNumber) setAnalyticsUser(accountNumber)
  } catch (error) {
    ErrorReporter.report(error)
  }
  TrackingService.sendEvent("Renewal - Entry New")
}

export const renewalEligibilityGuard: NavigationGuard = async () => {
  TrackingService.forceSessionReplayRecording()
  const queryClient = useQueryClient()

  try {
    const { accountIDLastVisited: accountId } =
      await queryClient.ensureQueryData(useGetCustomerQueryOptions())
    if (!accountId) throw new Error("accountId not found")

    // Parallel so tracking adds no load time; awaited so Entry precedes the outcome event
    const [, { canRenew, ineligibilityReasons }] = await Promise.all([
      trackEntry(queryClient, accountId),
      queryClient.fetchQuery(useGetRenewalEligibilityQueryOptions(accountId)),
    ])

    const ineligibleRoute = getIneligibleRenewalRoute(ineligibilityReasons)
    if (ineligibleRoute) {
      TrackingService.sendEvent(
        ineligibleRoute === MyAccountRoute.renewalPending
          ? "Renewal - Pending"
          : "Renewal - Ineligible",
      )
      return { path: ineligibleRoute }
    }

    if (!canRenew) {
      TrackingService.sendEvent("Renewal - Ineligible")
      return { path: MyAccountRoute.home }
    }
  } catch (error) {
    ErrorReporter.report(error)
  }
}
